import React, { useState } from 'react';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';

import QuestionBooleenne, { QuestionBoolChangeEvent } from './QualificationComponents/QuestionBooleenne';

import DropZone from 'components/Shared/DropZone';
import PieceGestionSearch from 'components/Shared/PieceGestionSearch';

import { ReclamationQualificationDetailCodes } from 'constants.js';
import { ReclamationQualificationDetailType } from 'constants.js';

import qualificationHelper from 'utils/qualificationHelper';

import {    Reclamation,
            QualificationDetail,
            BoolQualificationDetail,
            StringQualificationDetail,
            IntegerQualificationDetail,
            DateQualificationDetail
} from 'types/AddReclamation/reclamation';

import { Descriptor } from 'types/AddReclamation/descriptor';



// Type pour les props
type Props = {
    reclamation: Reclamation;
    onChange: (event: any, errors?: any[]) => void;
    onDetailChange: (detail: QualificationDetail | QualificationDetail[], errors?: any[], detailsToDelete?: string[]) => void;
    onSelectPiece: (value: any, errors: any[], isValid?: boolean) => void;
    onAddLigne: (ligne: any) => void;
    onRemoveLigne: (numeroLigne: string) => void;
    validationErrors?: any[];
    onNextStepValid: (isValid: boolean) => void;
    namespace: string;
};

function LivraisonSansPresenceStep({reclamation, onChange, onDetailChange, onSelectPiece, /*validationErrors,*/ onNextStepValid, namespace } : Props) {
    
    // États
    const [expandedPanel, setExpandedPanel] = useState<string>('panelPiece');


    // Effet pour initialiser la validation
    React.useEffect(() => {
        onNextStepValid(false);
    }, [onNextStepValid]);

    // Regles de validation à passer en paramètre de qualificationHelper.getErrors
    // La fonction d'assertion renvoi true quand la règle est respectée
    const _rules = [
        {
            key: 'numeroPiece',
            errorMsg: "Veuillez sélectionner une pièce.",
            fields: 'numeroPiece',
            assertion: function (a : any) {
                return a !== undefined && a !== null && a !== ''
            }
        },
        {
            key: ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeRecue,
            errorMsg: "Commande reçue ?",
            fields: ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeRecue,
            assertion: function (a : any) {
                return a !== undefined && a !== null && a !== ''
            }
        },
        {
            key: ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeEndommagee,
            errorMsg: "Commande endommagée ?",
            fields: [ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeRecue, ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeEndommagee],
            assertion: function (a : any) {
                return a[0] === "false" || (a[1] !== undefined && a[1] !== null && a[1] !== '');
            }
        },
        {
            key: ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeComplete,
            errorMsg: "Commande complète ?",
            fields: [ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeRecue, ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeComplete],
            assertion: function (a : any) {
                return a[0] === "false" || (a[1] !== undefined && a[1] !== null && a[1] !== '');
            }
        },
        {
            key: ReclamationQualificationDetailCodes.LivraisonSansPresenceSignalerTransporteur,
            errorMsg: "Voulez-vous signaler le transporteur ?",
            fields: ReclamationQualificationDetailCodes.LivraisonSansPresenceSignalerTransporteur,
            assertion: function (a : any) {
                return a !== undefined && a !== null && a !== ''
            }
        }
    ];

    // Obtention d'un détail quelconque.
    const getDetail = (code: string, typeValeur: 'valeurString' | 'valeurBool' | 'valeurDate' | 'valeurInteger') => {

        // Initialisation de la valeur
        let valeur: string | number | null | undefined = '';

        // Obtention du code
        const detail = reclamation.qualificationDetails.find(detail => detail.code === code);

        // Affectation si possible de la valeur en fonction du type demandé
        if (detail !== undefined) {
            switch (typeValeur) 
            {
                case 'valeurString' :
                    valeur = (detail as StringQualificationDetail).valeurString
                    break;
                case 'valeurBool' :
                    valeur = (detail as BoolQualificationDetail).valeurBool
                    break;
                case 'valeurDate' :
                    valeur = (detail as DateQualificationDetail).valeurDate
                    break;
                case 'valeurInteger' :
                    valeur = (detail as IntegerQualificationDetail).valeurInteger
                    break;                
            }
        }

        return valeur;
    }
    
    // Fonctions d'accès aux détails de qualification
    const getCommandeRecue = (): string | null => {
        return getDetail(ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeRecue, "valeurBool") as ('' | "0" | "1") || '';
    };
    
    const getCommandeComplete = (): string | null => {
        return getDetail(ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeComplete, "valeurBool") as ('' | "0" | "1") || '';
    };
    
    const getCommandeEndommagee = (): string | null => {
        return getDetail(ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeEndommagee, "valeurBool") as ('' | "0" | "1") || '';
    };
    
    const getSignalerTransporteur = (): string | null => {
        return getDetail(ReclamationQualificationDetailCodes.LivraisonSansPresenceSignalerTransporteur, "valeurBool") as ('' | "0" | "1") || '';
    };

    // Génére un tableau des champs à controler. Un champ peut être mis à jour par rapport aux valeur par défaut.
    const getToCheck = (champ: string | null, valeur: any | null) => {

        console.log('getToCheck', {champ, valeur});

        const result = {
            numeroPiece: reclamation.numeroPiece,
            [ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeRecue]: getCommandeRecue(),
            [ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeComplete]: getCommandeComplete(),
            [ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeEndommagee]: getCommandeEndommagee(),
            [ReclamationQualificationDetailCodes.LivraisonSansPresenceSignalerTransporteur]: getSignalerTransporteur(),
        }

        if (champ) {
            result[champ] = valeur;
        }

        console.log('getToCheck', result);

        return result;

    }

    // Fonctions

    const handleChangePanel = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanel(isExpanded ? panel : '');
    };

    const handleSelectPiece = (value: any) => {
        onNextStepValid(true);

        const toCheckValue = getToCheck('numeroPiece', value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onSelectPiece(value, errors, false);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        
        const toCheckValue = getToCheck(event.target.name, event.target.value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);
        
        onChange(event, errors);
    };

    const handleQuestionChange = (changeEvent: QuestionBoolChangeEvent) => {
        
        const qualificationDetail : BoolQualificationDetail = {
            position: changeEvent.position,
            code: changeEvent.code,
            type: ReclamationQualificationDetailType.Booleen,
            libelle: changeEvent.legende,
            valeurBool: changeEvent.value as ("" | "true" | "false"),
            valeurString: changeEvent.libelle
        };

        const toCheckValue = getToCheck(changeEvent.code, changeEvent.value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);
        const detailsToDelete: string[] = [];

        onDetailChange(qualificationDetail, errors, detailsToDelete);
    };


    // Fonctions de rendu
    const renderHeader = () => {
        return (
            <>
                <TextField
                    multiline
                    fullWidth
                    name="commentaire"
                    label="Commentaire"
                    placeholder="Décrire le problème rencontré"
                    maxRows={3}
                    onChange={handleChange}
                    value={reclamation.commentaire}
                    InputLabelProps={{
                        shrink: true
                    }}
                    sx={{ mb: 1}}
                />

                <Box  sx={{ mb: 1}}>
                    <DropZone id="fichiersIds" name="fichiersIds" onChange={handleChange} />
                </Box>

            </>
        );
    };

    const renderPieceGestionSearch = () => {
        return (
            <Accordion expanded={expandedPanel === 'panelPiece'} onChange={handleChangePanel('panelPiece')}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                >
                    <Typography>Rechercher une pièce*</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <PieceGestionSearch
                        onSelectPiece={handleSelectPiece}
                        client={reclamation.client}
                        withLignes={false}
                        namespace={namespace}
                        warningReclamation
                        defautProduitInfos={false}
                        logistiqueInfos={false}
                        defautNumeroPiece={reclamation.numeroPiece}
                    />
                </AccordionDetails>
            </Accordion>
        );
    };

    
    const renderCommandeRecueQuestion = () => {
        // Initialiser la variable locale pour éviter les appels multiples à getDetail
        const commandeRecue = getCommandeRecue();
        
        return (
            <QuestionBooleenne
                legende="Commande reçue ?"
                qualificationDetailCode={ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeRecue}
                value={commandeRecue}
                position={0}
                onChange={handleQuestionChange}
                required
            />
        );
    };

    const renderCommandeCompleteQuestion = () => {
        // Initialiser la variable locale pour éviter les appels multiples à getDetail
        const commandeComplete = getCommandeComplete();
        
        return (
            <QuestionBooleenne
                legende="Commande complète ?"
                qualificationDetailCode={ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeComplete}
                value={commandeComplete}
                position={1}
                onChange={handleQuestionChange}
                required
            />
        );
    };

    const renderCommandeEndommageeQuestion = () => {
        // Initialiser la variable locale pour éviter les appels multiples à getDetail
        const commandeEndommagee = getCommandeEndommagee();
        
        return (
            <QuestionBooleenne
                legende="Commande endommagée ?"
                qualificationDetailCode={ReclamationQualificationDetailCodes.LivraisonSansPresenceCommandeEndommagee}
                value={commandeEndommagee}
                position={2}
                onChange={handleQuestionChange}
                required
            />
        );
    };

    const renderSignalerTransporteurQuestion = () => {
        // Initialiser la variable locale pour éviter les appels multiples à getDetail
        const signalerTransporteur = getSignalerTransporteur();
        
        return (
            <QuestionBooleenne
                legende="Voulez-vous signaler le transporteur ?"
                qualificationDetailCode={ReclamationQualificationDetailCodes.LivraisonSansPresenceSignalerTransporteur}
                value={signalerTransporteur}
                position={3}
                onChange={handleQuestionChange}
                required
            />
        );
    };


    // Descripteur pour la machine d'état
    const mainDescriptor : Descriptor[] = [
        {
            name: 'header',
            renderer: renderHeader,
        },
        {
            name: 'pieceGestionSearch',
            renderer: renderPieceGestionSearch,
            getter: () => {
                return {
                    numeroPiece: reclamation.numeroPiece
                };
            },
            subComponents: [
                {
                    tester: (a: any) => a.numeroPiece !== '',
                    descriptor: [
                        {
                            renderer: renderCommandeRecueQuestion,
                            getter: () => {
                                return {
                                    commandeRecue: getCommandeRecue()
                                };
                            },
                            subComponents: [
                                {
                                    tester: (a: any) => a.commandeRecue === 'true',
                                    descriptor: {
                                        renderer: renderCommandeEndommageeQuestion
                                    }
                                },
                                {
                                    tester: (a: any) => a.commandeRecue === 'true',
                                    descriptor: {
                                        renderer: renderCommandeCompleteQuestion
                                    }
                                },                                
                                {
                                    tester: (a: any) => a.commandeRecue !== '',
                                    descriptor: {
                                        renderer: renderSignalerTransporteurQuestion
                                    }
                                },
                            ]
                        }
                    ]
                }
            ]
        }
    ];

    return qualificationHelper.getComponents(mainDescriptor);
}

export default LivraisonSansPresenceStep;
